import {
  CommonCollapseButton,
  CommonDeleteButton,
  CommonEditButton,
  CommonHeading,
  CommonNewButton,
  CommonSearch,
  CommonToggleButton,
  ConfirmDialog,
  customUseEffect,
  formatDateValue,
  getCreatedByCreatedOnUpdatedByUpdatedOnColumn,
  Grid,
  MuiDataGrid,
  TabHeaderLayout,
} from "@common";
import {
  getCrmTeamApi,
  getProjectUser,
  rowSelectCrmTeam,
} from "@features";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddPage from "./AddPage";
import UpdatePage from "./UpdatePage";

const sanitizeId = (id) => {
  if (id === true || id === false || id === "true" || id === "false" || id === undefined || id === null) {
    return null;
  }
  return id;
};

const getSelectedId = (data, fallbackId) => {
  const cleanFallback = sanitizeId(fallbackId);
  if (Array.isArray(data)) {
    return sanitizeId(data[0]?.id || data[0]?._id) || cleanFallback || null;
  }
  return sanitizeId(data?.id || data?._id) || cleanFallback || null;
};

const getRowUserName = (row, projectUsersOptions) => {
  const userObj =
    typeof row?.user === "object"
      ? row?.user
      : projectUsersOptions.find(
        (u) => u?.id === row?.userId || u?.userId === row?.userId
      );
  return (
    row?.userName ||
    userObj?.userName ||
    userObj?.email ||
    userObj?.firstName ||
    row?.userId ||
    ""
  );
};

const mapOriginalRows = (gridRow, projectUsersOptions) => {
  return (gridRow || []).filter(Boolean).map((row) => ({
    ...row,
    userName: getRowUserName(row, projectUsersOptions),
  }));
};

const isRequestSuccessful = (res) =>
  Boolean(
    res?.payload?.data?.ok ||
    res?.payload?.ok ||
    res?.meta?.requestStatus === "fulfilled" ||
    !res?.error
  );

const isDeleteSuccessful = (res) =>
  Boolean(
    res?.payload?.ok ||
    res?.payload?.data?.ok ||
    res?.meta?.requestStatus === "fulfilled"
  );

const Page = ({
  forCampaign,
  forProspect,
  forInquiry,
  forLead,
  forOpportunity,
  forAccount,
  forCustomer,
  forContact,
  forContract,
  forOrder,
  campaignId: propCampaignId,
  prospectId: propProspectId,
  inquiryId: propInquiryId,
  leadId: propLeadId,
  opportunityId: propOpportunityId,
  customerAccountId: propCustomerAccountId,
  customerId: propCustomerId,
  contactId: propContactId,
  contractId: propContractId,
  orderId: propOrderId,
  heading = "CRM Team",
  permissions = {},
  ...props
}) => {
  const dispatch = useDispatch();

  const [showAdd, setShowAdd] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [viewMore, setViewMore] = useState(true);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });

  const campaignSelectedData = useSelector(
    (state) => state.campaign?.selectedData
  );
  const prospectSelectedData = useSelector(
    (state) => state.prospect?.selectedData
  );
  const inquirySelectedData = useSelector(
    (state) => state.inquiry?.selectedData
  );
  const leadSelectedData = useSelector((state) => state.lead?.selectedData);
  const opportunitySelectedData = useSelector(
    (state) => state.opportunity?.selectedData
  );
  const accountSelectedData = useSelector(
    (state) => state.account?.selectedData
  );
  const customerSelectedData = useSelector(
    (state) => state.customer?.selectedData
  );
  const contactSelectedData = useSelector(
    (state) => state.contact?.selectedData
  );
  const contractSelectedData = useSelector(
    (state) => state.contract?.selectedData
  );
  const orderSelectedData = useSelector(
    (state) => state.order?.selectedData
  );

  const propsWithFlags = {
    forCampaign,
    forProspect,
    forInquiry,
    forLead,
    forOpportunity,
    forAccount,
    forCustomer,
    forContact,
    forContract,
    forOrder,
    campaignId: propCampaignId,
    prospectId: propProspectId,
    inquiryId: propInquiryId,
    leadId: propLeadId,
    opportunityId: propOpportunityId,
    customerAccountId: propCustomerAccountId,
    customerId: propCustomerId,
    contactId: propContactId,
    contractId: propContractId,
    orderId: propOrderId,
    ...props,
  };

  const api = useMemo(
    () => getCrmTeamApi(propsWithFlags),
    [
      forCampaign,
      forProspect,
      forInquiry,
      forLead,
      forOpportunity,
      forAccount,
      forCustomer,
      forContact,
      forContract,
      forOrder,
      propCampaignId,
      propProspectId,
      propInquiryId,
      propLeadId,
      propOpportunityId,
      propCustomerAccountId,
      propCustomerId,
      propContactId,
      propContractId,
      propOrderId,
      props?.entityType,
    ]
  );

  const activeEntityId = useMemo(() => {
    const rawPropId = sanitizeId(propsWithFlags?.[api.idKey]);
    if (rawPropId && (typeof rawPropId === "string" || typeof rawPropId === "number")) {
      return rawPropId;
    }
    let resolved = null;
    switch (api.entityType) {
      case "contract":
        resolved = sanitizeId(propContractId) || getSelectedId(contractSelectedData);
        break;
      case "customer-account":
        resolved = sanitizeId(propCustomerAccountId) || getSelectedId(accountSelectedData);
        break;
      case "contact":
        resolved = sanitizeId(propContactId) || getSelectedId(contactSelectedData);
        break;
      case "opportunity":
        resolved = sanitizeId(propOpportunityId) || getSelectedId(opportunitySelectedData);
        break;
      case "lead":
        resolved = sanitizeId(propLeadId) || getSelectedId(leadSelectedData);
        break;
      case "campaign":
        resolved = sanitizeId(propCampaignId) || getSelectedId(campaignSelectedData);
        break;
      case "inquiry":
        resolved = sanitizeId(propInquiryId) || getSelectedId(inquirySelectedData);
        break;
      case "prospect":
        resolved = sanitizeId(propProspectId) || getSelectedId(prospectSelectedData);
        break;
      case "customer":
        resolved = sanitizeId(propCustomerId) || getSelectedId(customerSelectedData);
        break;
      case "order":
        resolved = sanitizeId(propOrderId) || getSelectedId(orderSelectedData);
        break;
      default:
        resolved = null;
    }
    return sanitizeId(resolved);
  }, [
    api.entityType,
    api.idKey,
    propsWithFlags,
    propContractId,
    propCustomerAccountId,
    propContactId,
    propOpportunityId,
    propLeadId,
    propCampaignId,
    propInquiryId,
    propProspectId,
    propCustomerId,
    propOrderId,
    contractSelectedData,
    accountSelectedData,
    contactSelectedData,
    opportunitySelectedData,
    leadSelectedData,
    campaignSelectedData,
    inquirySelectedData,
    prospectSelectedData,
    customerSelectedData,
    orderSelectedData,
  ]);

  const selectedCrmTeam = useSelector(
    (state) => state.crmTeam?.selectedData
  );

  const selectedCrmTeamId = getSelectedId(selectedCrmTeam);

  const gridRow = useSelector((state) => state.crmTeam?.data) || [];
  const projectUsersOptions =
    useSelector((state) => state?.projectUsers?.data) || [];

  const originalRows = useMemo(
    () => mapOriginalRows(gridRow, projectUsersOptions),
    [gridRow, projectUsersOptions]
  );

  const [filteredRows, setFilteredRows] = useState(originalRows);

  customUseEffect(() => {
    setFilteredRows(originalRows);
  }, [originalRows]);

  const getFunction = useCallback(() => {
    const cleanEntityId = sanitizeId(activeEntityId);
    if (cleanEntityId) {
      dispatch(api.get({ [api.idKey]: cleanEntityId }));
    } else {
      dispatch(api.get());
    }
    dispatch(getProjectUser());
  }, [dispatch, api, activeEntityId]);

  customUseEffect(() => {
    setViewMore(true);
    getFunction();
    return () => dispatch(rowSelectCrmTeam([{}]));
  }, [activeEntityId, api.entityType, getFunction, dispatch]);

  const handleTogglePrimary = useCallback(
    async (e, params) => {
      e.stopPropagation();
      const cleanEntityId = sanitizeId(activeEntityId);
      const payload = {
        id: params?.id,
        isPrimary: !params?.isPrimary,
        ...(cleanEntityId ? { [api.idKey]: cleanEntityId } : {}),
      };
      const res = await dispatch(api.update(payload));
      if (isRequestSuccessful(res)) {
        getFunction();
      }
    },
    [dispatch, api, getFunction, activeEntityId]
  );

  const handleDelete = useCallback(
    (id) => {
      dispatch(api.delete({ id })).then((res) => {
        if (isDeleteSuccessful(res)) {
          getFunction();
          setConfirmDialog({ isOpen: false, title: "", subTitle: "" });
          dispatch(rowSelectCrmTeam([{}]));
        }
      });
    },
    [dispatch, api, getFunction]
  );

  const handleDeleteClick = useCallback(
    (row) => {
      setConfirmDialog({
        isOpen: true,
        title: "Are you sure to delete this record?",
        subTitle: "You can't undo this operation",
        onConfirm: () => handleDelete(row?.id),
      });
    },
    [handleDelete]
  );

  const data = useMemo(
    () => ({
      rows: filteredRows || [],
      columns: [
        {
          field: "assignmentType",
          headerName: "Assignment Type",
          renderHeader: () => <strong>Assignment Type</strong>,
          width: 250,
        },
        {
          field: "displayName",
          headerName: "User Name",
          renderHeader: () => <strong>User Name</strong>,
          valueGetter: (value, row) => {
            const fName = row?.user_detail?.firstName || "";
            const lName = row?.user_detail?.lastName || "";
            return `${fName} ${lName}`.trim() || row?.userName || "";
          },
          width: 250,
        },
        {
          field: "userName",
          headerName: "User Email",
          renderHeader: () => <strong>User Email</strong>,
          width: 300,
        },
        {
          field: "isPrimary",
          headerName: "Primary",
          headerAlign: "center",
          align: "center",
          renderHeader: () => <strong>Primary</strong>,
          renderCell: (value) => {
            const params = value?.row;
            return (
              <CommonToggleButton
                onClick={(e) => handleTogglePrimary(e, params)}
                checked={Boolean(params?.isPrimary)}
                disabled={!permissions?.update}
                tooltip={params?.isPrimary ? "True" : "False"}
              />
            );
          },
          width: 120,
        },
        permissions?.update && {
          field: "Edit",
          type: "actions",
          headerName: "Edit",
          renderHeader: () => <strong>Edit</strong>,
          width: 50,
          renderCell: (value) => {
            const row = value?.row;
            return (
              <CommonEditButton
                handleClick={() => {
                  setSelectedData(row);
                  setShowUpdate(true);
                }}
                permission={permissions?.update}
              />
            );
          },
        },
        permissions?.delete && {
          field: "Delete",
          type: "actions",
          headerName: "Delete",
          renderHeader: () => <strong>Delete</strong>,
          width: 80,
          renderCell: (value) => {
            const row = value?.row;
            return (
              <CommonDeleteButton
                handleClick={() => handleDeleteClick(row)}
                permission={permissions?.delete}
              />
            );
          },
        },
        {
          field: "startDate",
          headerName: "Start Date",
          renderHeader: () => <strong>Start Date</strong>,
          valueGetter: (value, row) => row?.startDate,
          valueFormatter: (value) => formatDateValue(value, true),
          sortComparator: (v1, v2) => new Date(v1) - new Date(v2),
          width: 180,
        },
        {
          field: "endDate",
          headerName: "End Date",
          renderHeader: () => <strong>End Date</strong>,
          valueGetter: (value, row) => row?.endDate,
          valueFormatter: (value) => formatDateValue(value, true),
          sortComparator: (v1, v2) => new Date(v1) - new Date(v2),
          width: 180,
        },
        ...getCreatedByCreatedOnUpdatedByUpdatedOnColumn(),
      ],
    }),
    [
      filteredRows,
      permissions?.update,
      permissions?.delete,
      handleTogglePrimary,
      handleDeleteClick,
    ]
  );

  const showAddFunction = () => setShowAdd(true);

  return (
    <div>
      <TabHeaderLayout>
        <Grid
          size={{
            md: 2.5,
            lg: 2.5,
            xs: 2.5,
            xl: 2.5,
            sm: 2.5,
          }}
          style={{
            display: "flex",
            justifyContent: "left",
          }}
        >
          <CommonCollapseButton
            viewMore={viewMore}
            setViewMore={setViewMore}
          />
          {permissions?.create && (
            <CommonNewButton handleNew={showAddFunction} />
          )}
        </Grid>
        <Grid
          size={{
            md: 6,
            lg: 6,
            xs: 6,
            xl: 6,
            sm: 6,
          }}
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CommonHeading heading={heading} />
        </Grid>
        <Grid
          size={{
            md: 3.3,
            lg: 3.3,
            xs: 3.5,
            xl: 3.3,
            sm: 3.5,
          }}
          style={{
            display: "flex",
            justifyContent: "right",
          }}
        >
          <CommonSearch
            rows={originalRows}
            searchFields={[
              "assignmentType",
              "userName",
              "startDate",
              "endDate",
              "isPrimary",
            ]}
            onFilter={setFilteredRows}
          />
        </Grid>
      </TabHeaderLayout>

      <Grid container spacing={0}>
        {viewMore && (
          <Grid size={{ sm: 12, md: 12 }}>
            <MuiDataGrid
              data={data}
              select={(selectedRows) => {
                permissions?.details && dispatch(rowSelectCrmTeam(selectedRows));
              }}
            />
          </Grid>
        )}
      </Grid>

      {showAdd && (
        <AddPage
          showAdd={showAdd}
          close={() => setShowAdd(false)}
          getFunction={getFunction}
          forCampaign={forCampaign}
          forProspect={forProspect}
          forInquiry={forInquiry}
          forLead={forLead}
          forOpportunity={forOpportunity}
          forAccount={forAccount}
          forCustomer={forCustomer}
          forContact={forContact}
          forContract={forContract}
          forOrder={forOrder}
          entityType={api.entityType}
          {...(sanitizeId(activeEntityId) ? { [api.idKey]: sanitizeId(activeEntityId) } : {})}
        />
      )}
      {showUpdate && (
        <UpdatePage
          showUpdate={showUpdate}
          rowData={selectedData}
          selectedData={selectedData}
          close={() => {
            setShowUpdate(false);
            setSelectedData(null);
          }}
          getFunction={getFunction}
          forCampaign={forCampaign}
          forProspect={forProspect}
          forInquiry={forInquiry}
          forLead={forLead}
          forOpportunity={forOpportunity}
          forAccount={forAccount}
          forCustomer={forCustomer}
          forContact={forContact}
          forContract={forContract}
          forOrder={forOrder}
          entityType={api.entityType}
          {...(sanitizeId(activeEntityId) ? { [api.idKey]: sanitizeId(activeEntityId) } : {})}
        />
      )}

      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
    </div>
  );
};

export default Page;
